import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptorFn,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, catchError, switchMap, throwError, Subject, take } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { environment } from 'apps/iea-app/src/environments/environment';

let isRefreshing = false;
const tokenSubject: Subject<string> = new Subject<string>();

export const TokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  // ⛔ Skip interceptor for S3 uploads
  if (req.method === 'PUT' && req.url.includes('.s3.amazonaws.com')) {
    console.log("returning from here");
    return next(req); // don't touch headers at all
  }

  const authService = inject(AuthService);
  const snackBar = inject(MatSnackBar)
  const router = inject(Router)
  const sessionData: any = authService.getSessionData();

  const skipAuthTokenUrls = [
    "/login",
    "/login/verifyToken",
    "/signup/create",
    "/signup/isEmailExists",
    "/signup/isMobileExists",
    "/otp/generate",
    "/otp/verify",
    "/password/isEmailExists",
    "/password/sendResetLink",
    "/password/verify",
    "/password/update",
    "/admin/getAdmin",
    "/seasons/list",
    "/IEA",
    "/orders",
    "/payments",
    "/carts",
    "/glCodes",
    "/plans",
    "/products",
    "/product-categories"
  ];
  const apiUrl = environment.apiUrl
    
  const foundInSkipUrl = skipAuthTokenUrls.find(url=> `${apiUrl}${url}` ==  req.url);
  if (foundInSkipUrl || req.url.includes("/IEA/")) {
    const authReq = req.clone({
      setHeaders: {
        uuid: `${sessionData.uuid}`,
        application: `${sessionData.application}`,
      },
    });
    return next(authReq);
  }
  const userTokens: any = JSON.parse(authService.getToken() as string);
  
  let modifiedReq = req;

  if (!(req.body instanceof FormData)) {
    modifiedReq = req.clone({
      setHeaders: {
        'Content-Type': 'application/json; charset=utf-8',
        Accept: 'application/json',
      },
    });
  }
  modifiedReq = modifiedReq.clone({
    setHeaders: {
      Authorization: `Bearer ${userTokens?.accessToken}`,
    },
  });
  
  return next(modifiedReq).pipe(
    catchError((error: any | HttpErrorResponse) => {
      if ((error.status === 401 && error.error.message === "Token Expired") ||
          (error.status === 401 && error?.message?.includes("report")) ||
          (error.status === 403 && error.error.message === "Token Expired")) {
        return handle401Error(req, next, authService, snackBar);
      }
      if (error.status === 403 && error.error.message === "Token Invalid") {
        authService.removeAccessToken();
        authService.cleanLocalStorage();
        snackBar.open(error.error.message, 'close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        })
        router.navigate(['/account/login'])
      }
      if (error.status === 403 && error.error.message === "Please login again!") {
        authService.removeAccessToken();
        authService.removeUserData();
        authService.removeRefreshToken();
        authService.removeTokens();
        authService.emitLoggedinUser(null);
        authService.cleanLocalStorage();
        snackBar.open(error.error.message, 'close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        })
        router.navigate(['/account/login'])
      }
      else if (error.status === 403) {
        authService.removeAccessToken();
        authService.removeUserData();
        authService.removeRefreshToken();
        authService.removeTokens();
        authService.cleanLocalStorage();
        snackBar.open(error.error.message, 'close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
        router.navigate(['/account/login']);
      }
      return throwError(error);

    })
  )
};

function handle401Error(req: HttpRequest<any>, next: HttpHandlerFn, authService: AuthService, snackBar: MatSnackBar): Observable<any> {
  if (!isRefreshing) {
    isRefreshing = true;
    tokenSubject.next('');
    const userTokens: any = JSON.parse(authService.getToken() as string);

    return authService.getNewAccessToken({ refreshToken: userTokens.refreshToken }).pipe(
      switchMap((newTokens: any) => {
        isRefreshing = false;
        tokenSubject.next(newTokens.accessToken);
        authService.setToken(JSON.stringify(newTokens));
        authService.setAccessToken(JSON.stringify(newTokens.accessToken));
        snackBar.open('Token Refreshed successfully!', 'close', {
          duration: 2000,
          panelClass: ['success-snackbar']
        });
        return next(handleRequestWithToken(req, newTokens.accessToken));
      }),
      catchError((error: any) => {
        isRefreshing = false;
        console.error("Error refreshing token:", error);
        authService.removeAccessToken();
        return throwError(error);
      })
    );
  } else {
    return tokenSubject.pipe(
      take(1),
      switchMap(token => {
        return next(handleRequestWithToken(req, token));
      })
    );
  }
}

function handleRequestWithToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}